/**
 * Opt-in DataForSEO (AB#1035): the owner brings their own DataForSEO login. With
 * none saved the backend never calls DataForSEO and this is one quiet link. The
 * login is write-only: nothing about it is ever read back into the form.
 * Lives under the Site health card, not in it, so the card stays free-first.
 */

import { useState } from 'react';
import {
  ApiError,
  useDeleteDataForSeoKey,
  useRefreshSeoInsights,
  useAccounts,
  useSaveDataForSeoKey,
  useSite,
  useSiteHealth,
  type DataForSeoKeyScope,
} from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { confirmDialog } from '@/lib/dialog';

const QUIET_LINK =
  'rounded-sm text-[13px] text-muted-foreground underline-offset-4 outline-none focus-ring hover:underline disabled:opacity-50';

const ownerOnly = (err: unknown) => err instanceof ApiError && err.status === 403;

export function DataForSeoKey({ siteSlug }: { siteSlug: string }) {
  const { data } = useSiteHealth(siteSlug);
  const save = useSaveDataForSeoKey(siteSlug);
  const refresh = useRefreshSeoInsights(siteSlug);
  const remove = useDeleteDataForSeoKey(siteSlug);
  const { data: site } = useSite(siteSlug);
  const { data: accounts } = useAccounts();
  // Saving, refreshing and removing spend or change the owner's DataForSEO account: owner only (backend enforces it too).
  const isOwner = accounts?.data?.find((a) => a.pid === site?.data?.account_pid)?.role === 'owner';
  const [open, setOpen] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [scope, setScope] = useState<DataForSeoKeyScope>('account');
  const [error, setError] = useState<string | null>(null);

  const dfs = data?.data?.dataforseo;
  if (!dfs) return null;

  if (dfs.configured) {
    const removeKey = async () => {
      const ok = await confirmDialog('Remove your DataForSEO key? Site health on all your sites stops showing Google rankings and AI mentions.', {
        destructive: true,
        confirmLabel: 'Remove key',
      });
      if (!ok) return;
      setError(null);
      try {
        // shortcut: removes both the site key and the account key, whichever exist.
        await remove.mutateAsync('site');
        await remove.mutateAsync('account');
      } catch (err) {
        setError(ownerOnly(err) ? 'Only the account owner can remove the key' : "We couldn't remove the key");
      }
    };
    const runCheck = () => {
      setError(null);
      refresh.mutate(undefined, { onError: () => setError("We couldn't run the check. Try again in a minute") });
    };
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-[13px] text-muted-foreground">
          {isOwner
            ? 'Using your own DataForSEO account for Google and AI data.'
            : "Google and AI data come from the account owner's DataForSEO account."}
        </p>
        {(error ?? dfs.error) && <p role="status" className="text-[13px] text-muted-foreground">{error ?? dfs.error}</p>}
        {isOwner && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <button type="button" className={QUIET_LINK} disabled={refresh.isPending} onClick={runCheck}>
              {refresh.isPending ? 'Checking your site…' : 'Run a fresh site check (uses DataForSEO credit)'}
            </button>
            <button type="button" className={QUIET_LINK} disabled={remove.isPending} onClick={removeKey}>
              Remove key
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!isOwner) return null;

  if (!open) {
    return (
      <button type="button" className={`${QUIET_LINK} self-start text-left`} onClick={() => setOpen(true)}>
        Have a DataForSEO account? Add your own key for rankings and AI mentions
      </button>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({ login, password, scope });
      setOpen(false);
      setLogin('');
      setPassword('');
    } catch (err) {
      setError(ownerOnly(err) ? 'Only the account owner can add a key' : err instanceof Error ? err.message : "We couldn't save that key");
    }
  };

  return (
    <form onSubmit={submit} aria-label="Add your DataForSEO key" className="flex flex-col gap-3">
      <p className="text-[13px] text-muted-foreground text-pretty">
        DataForSEO bills your own account for each check. We store the key encrypted and never show it again.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dfs-login">DataForSEO API login</Label>
          <Input id="dfs-login" autoFocus autoComplete="off" value={login} onChange={(e) => setLogin(e.target.value)} required />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dfs-password">API password</Label>
          <Input id="dfs-password" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
      </div>
      <fieldset className="text-sm">
        <legend className="mb-1 text-[13px] text-muted-foreground">Use this key for</legend>
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          {([['account', 'All my sites'], ['site', 'Just this site']] as const).map(([value, text]) => (
            <label key={value} className="flex items-center gap-2">
              <input
                type="radio"
                name="dfs-scope"
                value={value}
                checked={scope === value}
                onChange={() => setScope(value)}
                className="accent-primary outline-none focus-ring"
              />
              {text}
            </label>
          ))}
        </div>
      </fieldset>
      {error && <p role="alert" className="text-[13px] text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending} className="rounded-[10px]">
          {save.isPending ? 'Checking…' : 'Save'}
        </Button>
        <Button type="button" variant="ghost" className="rounded-[10px]" onClick={() => { setOpen(false); setError(null); setLogin(''); setPassword(''); }}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
